// Loads the whole filter index once and keeps it, so changing a filter needs no request.
(function () {
  'use strict';

  const DB_NAME = 'sa-catalog';
  const STORE = 'index';
  const RECORD = 'catalog';

  function openDb() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('no indexedDB'));
        return;
      }
      const request = window.indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async function readCache() {
    try {
      const db = await openDb();
      return await new Promise((resolve) => {
        const tx = db.transaction(STORE, 'readonly');
        const get = tx.objectStore(STORE).get(RECORD);
        get.onsuccess = () => resolve(get.result || null);
        get.onerror = () => resolve(null);
      });
    } catch (error) {
      return null;
    }
  }

  async function writeCache(payload) {
    try {
      const db = await openDb();
      await new Promise((resolve) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(payload, RECORD);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      });
    } catch (error) {
      /* storage can be blocked, and the index still works without the cache */
    }
  }

  // Must match slug() in the app, which built the value keys.
  function slug(value) {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // List values stay as numbers, so filtering compares numbers and not text.
  function decode(payload) {
    const columns = {};
    payload.columns.forEach((name, at) => {
      columns[name] = at;
    });

    const products = payload.products.map((row) => ({
      id: row[columns.id],
      handle: row[columns.handle],
      title: row[columns.title],
      sku: row[columns.sku],
      variantId: row[columns.variantId],
      trim: row[columns.trim],
      price: row[columns.price],
      compareAt: row[columns.compareAt],
      image: row[columns.image] ? payload.imageBase + row[columns.image] : null,
      imageAlt: row[columns.imageAlt],
      swatchName: row[columns.swatchName],
      swatchColor: row[columns.swatchColor],
      swatchImage: row[columns.swatchImage],
      group: row[columns.group] || [],
      collections: new Set(row[columns.collections] || []),
      tags: row[columns.tags] || [],
      ranges: row[columns.ranges] || [],
      lists: row[columns.lists] || [],
      specs: row[columns.specs] || []
    }));

    const byId = new Map();
    products.forEach((product) => byId.set(String(product.id), product));

    const stringIndex = new Map();
    payload.strings.forEach((value, at) => {
      if (!stringIndex.has(value)) {
        stringIndex.set(value, at);
      }
    });

    const rangeAt = {};
    payload.rangeKeys.forEach((key, at) => {
      rangeAt[key] = at;
    });
    const listAt = {};
    payload.listKeys.forEach((key, at) => {
      listAt[key] = at;
    });
    const specAt = {};
    payload.specKeys.forEach((key, at) => {
      specAt[key] = at;
    });

    return {
      version: payload.version,
      builtAt: payload.builtAt,
      count: payload.count,
      imageBase: payload.imageBase,
      strings: payload.strings,
      facets: payload.facets,
      rangeKeys: payload.rangeKeys,
      listKeys: payload.listKeys,
      specKeys: payload.specKeys,
      rangeAt: rangeAt,
      listAt: listAt,
      specAt: specAt,
      products: products,
      byId: byId,
      text: function (code) {
        return code === -1 || code === undefined ? '' : payload.strings[code] || '';
      },
      code: function (value) {
        const at = stringIndex.get(String(value));
        return at === undefined ? -1 : at;
      }
    };
  }

  // 503 means no index is stored yet, so the caller keeps the grid Liquid rendered.
  function notBuilt() {
    const error = new Error('catalog index is not built yet');
    error.notBuilt = true;
    return error;
  }

  async function load(options) {
    const base = (options && options.proxy) || '/apps/halcyon-wishlist';
    const cached = await readCache();

    let remote = null;
    try {
      const response = await fetch(base + '/catalog-version', { credentials: 'same-origin' });
      if (response.ok) {
        remote = await response.json();
      }
    } catch (error) {
      /* the app may be down, and a cached index is better than none */
    }

    if (cached && cached.version && remote && cached.version === remote.version) {
      return { index: decode(cached), meta: remote, fromCache: true };
    }

    let payload = null;
    try {
      const response = await fetch(base + '/catalog-index', { credentials: 'same-origin' });
      if (response.status === 503) {
        throw notBuilt();
      }
      if (!response.ok) {
        throw new Error('index request failed with ' + response.status);
      }
      payload = await response.json();
    } catch (error) {
      if (cached && cached.products) {
        return { index: decode(cached), meta: remote, fromCache: true, stale: true };
      }
      throw error;
    }

    writeCache(payload);
    return { index: decode(payload), meta: remote, fromCache: false };
  }

  window.SACatalogIndex = { load: load, decode: decode, slug: slug };
})();
